/* eslint-disable no-use-before-define */

const getBaseConfig = require('../common/getBaseConfig');
const { dispatchReadyEvent } = require('../common/readyEvents');
const onDOMContentLoaded = require('../utilities/dom/onDOMContentLoaded');

const config = getBaseConfig().bambuser;

/**
 * Configures the Bambuser Live Shopping miniplayer behavior and position.
 *
 * This function adjusts the player's button layout, default minimized
 * position, and dynamically updates its offset depending on viewport
 * (mobile or desktop) and the current minimized corner.
 *
 * The miniplayer's position offsets are applied with positive `x`/`y`
 * values, but are internally adjusted to match the selected corner:
 * - Bottom-left  → `{ x: +x, y: −y }`
 * - Bottom-right → `{ x: −x, y: −y }`
 * - Top-left     → `{ x: +x, y: +y }`
 * - Top-right    → `{ x: −x, y: +y }`
 *
 * @function configureMiniplayer
 * @param {Object} player The Bambuser Live Shopping player instance.
 * @param {Function} player.configure Method to apply configuration options.
 * @param {Object} player.BUTTON Enum of player button constants.
 * @param {Object} player.MINIMIZED_POSITION Enum of miniplayer positions.
 * @param {Object} player.EVENT Enum of available player events.
 *
 * @description
 * - Sets the dismiss button to `MINIMIZE`.
 * - Defaults the miniplayer to the bottom-right corner.
 * - Listens to the `MINIMIZE` event and repositions the miniplayer
 *   according to the device type (mobile vs desktop).
 *
 * @example
 * // Usage after Bambuser script has loaded:
 * loadBambuserScript().then(() => {
 *   configureMiniplayer(window.bambuserModule);
 * });
 */
function configureMiniplayer(player) {
    player.configure({
        buttons: { dismiss: player.BUTTON.MINIMIZE },
        minimizedPosition: player.MINIMIZED_POSITION.BOTTOM_RIGHT
    });

    /**
   * Adjusts the miniplayer offset based on current minimized corner.
   * @param {{x:number, y:number}} coords Positive offset values.
   * @returns {void}
   */
    function setMiniPlayerOffset(coords) {
        // eslint-disable-next-line no-underscore-dangle
        var current = window.__currentBambuserLiveShoppingOverlayPlayer;
        var mini = current && current.miniPlayer;
        if (!mini || !mini.position) return;

        var pos = current.config.minimizedPosition;
        var x = coords.x;
        var y = coords.y;

        if (pos === player.MINIMIZED_POSITION.BOTTOM_LEFT) {
            mini.position = { x: +x, y: -y };
        } else if (pos === player.MINIMIZED_POSITION.BOTTOM_RIGHT) {
            mini.position = { x: -x, y: -y };
        } else if (pos === player.MINIMIZED_POSITION.TOP_LEFT) {
            mini.position = { x: +x, y: +y };
        } else if (pos === player.MINIMIZED_POSITION.TOP_RIGHT) {
            mini.position = { x: -x, y: +y };
        }
    }

    player.on(player.EVENT.MINIMIZE, function () {
        var isMobile = window.matchMedia(
            'only screen and (max-width: 760px)'
        ).matches;
        setMiniPlayerOffset(
            isMobile
                ? config.miniPlayer.offsets.mobile
                : config.miniPlayer.offsets.desktop
        );
    });
}

/**
 * Initializes a Bambuser Live Shopping show on the specified DOM node.
 * Requires that the Bambuser script has already been loaded.
 *
 * @function initBambuserShow
 * @param {Object} params - The Bambuser show ID to embed.
 * @param {string} params.showId - The Bambuser show ID to embed.
 * @param {HTMLElement} params.node - The DOM element where the show will be
 *  attached.
 * @param {"overlay" | "dock" | "embed"} [params.type="overlay"] - The embed
 *  type (overlay, dock, or embed).
 * @param {boolean} [params.enableMiniPlayer = false] if true, the miniplayer
 *  will be configured
 */
function initBambuserShow({
    showId,
    node,
    type = 'overlay',
    enableMiniPlayer = false
}) {
    loadBambuserScript().then(function () {
        if (typeof window.initBambuserLiveShopping === 'function') {
            if (enableMiniPlayer) {
                window.onBambuserLiveShoppingReady = configureMiniplayer;
            }
            window.initBambuserLiveShopping({
                showId,
                node,
                type
            });
        } else {
            throw new Error(
                'Bambuser script not loaded. Call loadBambuserScript() first.'
            );
        }
    });
}

/**
 * Loads the Bambuser Floating Action Button (FAB) widget.
 * This function dynamically injects the Bambuser widget script
 * into the DOM if it is not already present.
 *
 * @function loadFloatingActionButton
 * @param {string} fabId - The unique widget ID provided by Bambuser.
 * @see https://bambuser.com/docs/developer-resources/floating-action-button
 */
function initBambuserFloatingActionButton(fabId) {
    if (!fabId) return;

    loadBambuserScript().then(function () {
        const offsets = config.floatingActionButton.offsets;
        /* eslint-disable consistent-return, no-underscore-dangle */
        (function (d, t, i, w) {
            window.__bfwId = w;
            window.__bfwOffsX = [offsets.mobile.x, offsets.desktop.x];
            window.__bfwOffsY = [offsets.mobile.y, offsets.desktop.y];
            if (d.getElementById(i) && window.__bfwInit) return window.__bfwInit();
            if (d.getElementById(i)) return;
            const s = d.createElement(t);
            const ss = d.getElementsByTagName(t)[0];
            s.id = i;
            s.src = 'https://lcx-widgets-eu.bambuser.com/embed.js';
            ss.parentNode.insertBefore(s, ss);
        }(document, 'script', 'bambuser-liveshopping-widget', fabId));
        /* eslint-enable consistent-return, no-underscore-dangle  */
    });
}

const bambuserModule = {
    initBambuserShow,
    initBambuserFloatingActionButton
};

/**
 * Loads the Bambuser Live Shopping embed script if it's not already present.
 * This function creates the global `window.initBambuserLiveShopping` queue
 * and injects the external Bambuser script into the DOM.
 *
 * @function loadBambuserScript
 * @see https://bambuser.com/docs/developer-resources
 * @returns {Promise} Resolves when the script is loaded
 */
function loadBambuserScript() {
    return new Promise(function (resolve) {
        if (window.initBambuserLiveShopping) {
            resolve();
        } else {
            window.initBambuserLiveShopping = function (item) {
                window.initBambuserLiveShopping.queue.push(item);
            };
            window.initBambuserLiveShopping.queue = [];

            const scriptNode = document.createElement('script');
            scriptNode.src = 'https://lcx-embed-eu.bambuser.com/default/embed.js';

            scriptNode.addEventListener('load', function () {
                resolve();
            });
            document.body.appendChild(scriptNode);
        }
    });
}

/**
 * Initializes the module
 */
(function () {
    onDOMContentLoaded(function () {
        loadBambuserScript().then(function () {
            initBambuserFloatingActionButton(
                config.floatingActionButton.id
            );
            dispatchReadyEvent('ADBambuser', bambuserModule);
        });
    });
}());

module.exports = bambuserModule;
